import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {Box, Button, CircularProgress, IconButton, Stack, TextField} from "@mui/material";
import {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import axios, {setBaseURL} from "../../axios/axios";
import useWebSocket from "../../hooks/useWebSocket";
import {MessageBox} from "../chat-preview/MessageBox";
import {toast} from "react-toastify";
import {Send as IconMessage} from "@mui/icons-material"

export const ChatbotConversation = React.memo(() => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const {flowUid, chatId: id} = useParams();
  const [updatedValue, state] = useWebSocket(`/topic/session/`, id);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef();
  const conversation_logger = localStorage.getItem("conversation_logger") || process.env.REACT_APP_GATEWAY;

  useEffect(() => {
    setBaseURL(conversation_logger);
  }, [conversation_logger])

  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const interval = setInterval(() => {
        fetchMessages();
        fetchConversation();
      }, 3000)
      return () => {
        clearInterval(interval)
      }
    } else {

      if (flowUid && !id) {
        createNewConversation()
      }
      setMessages([]);
    }
  }, [id, flowUid])

  const createNewConversation = async () => {
    if (!flowUid) {
      return;
    }
    setLoading(true);
    try {
      const phonenumber = localStorage.getItem("phonenumber") || "0038970581901";
      const response = await axios.post(`/api/conversation`, {
        json: null,
        flowUid: flowUid || null,
        sender: phonenumber,
        conversationType: "CHAT_BOT"
      })
      localStorage.setItem('phonenumber', phonenumber)
      navigate(`/project/${flowUid}/chatbot/${response?.data?.id}`);
    } catch (e) {
      toast.error(e.message);
      navigate("/not-found")
    } finally {
      setLoading(false);
    }
  }

  const fetchConversation = () => {
    axios.get(`/api/conversation/${id}`)
      .then(response => {
        setConversation(response?.data);
      })
      .catch((error) => console.log(error?.message))
  }

  const fetchMessages = () => {
    axios.get(`/api/conversation/${id}/messages`)
      .then(response => {
        setMessages(response.data);
      })
      .catch(error => {
        console.log(error);
      })
  }


  useEffect(() => {
    if (updatedValue && +id === +updatedValue?.session) {
      const newMessage = updatedValue.message;
      setMessages(prevState => {
        const items = [...prevState];
        const index = items.findIndex(i => i.id === newMessage.id);
        if (isFinite(index) && index >= 0) {
          items[index] = newMessage;
        } else {
          items.push(newMessage);
        }
        return items.sort((p1, p2) => (p1.dateCreated > p2.dateCreated) ? 1 : (p1.dateCreated < p2.dateCreated) ? -1 : 0)
      });
      fetchConversation();
    }
  }, [updatedValue])


  const sendMessage = useCallback(async () => {
    if (message) {
      setLoading(true);
      try {
        const data = {
          session: id,
          conversation: id,
          hotwords: "",
          audio: "",
          message: message,
        }
        setMessage("");
        const response = await axios.post(`${conversation_logger}/api/conversation/${conversation?.id}/send`, data)
        fetchMessages();
        console.log(response);
      } catch (e) {
        console.log(e);
        toast.error("Failed to send message");
      } finally {
        setLoading(false);
      }
    }
  }, [conversation_logger])

  const filteredMessages = useMemo(() => {
    return messages?.filter(message => message?.text && ["AGENT", "USER"].includes(message?.role)) || []
  }, [messages])

  return (
    <>
      <Stack direction={"row"} gap={1} sx={{position: 'relative', maxHeight: "calc(100dvh - 60px)", height: "100%"}}>
        <Stack direction={"column"} sx={{
          maxHeight: "calc(100dvh - 1px)", height: "100dvh",
          flex: 1,
        }}>
          <Box
            sx={{
              zIndex: -1,
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-30%, -100%)",
              backdropFilter: "blur(10px)",
            }}
          >
            <img src={"/logo.svg"} alt={"Logo"} width={"60%"} height={"auto"}
                 style={{filter: "brightness(130%) blur(10px)"}}/>
          </Box>
          <Box sx={{flex: 1, p: 2, overflowY: 'auto', zIndex: 10}}>
            <Stack direction={"column"} maxWidth={"md"} sx={{mx: 'auto'}} gap={1}>
              {
                filteredMessages?.map((message, index) => {
                  const prev = filteredMessages[index - 1] || null;

                  return (
                    <MessageBox
                      key={message?.id || index}
                      message={message} prevMessage={prev}
                      selectedMessage={null}
                      onSelectMessage={() => {
                      }}
                      hideStats={true}
                    />
                  )
                })
              }
              <div ref={bottomRef}/>
            </Stack>
          </Box>

          {conversation?.status === "ACTIVE" ?
            <>
              <Stack maxWidth={"sm"} direction={"row"} alignItems={"center"} justifyContent={"center"}
                     className={"border-shadow"} gap={2}
                     sx={{borderRadius: 2, py: 2, px: 2, width: "calc(90% - 32px)", mx: "auto"}}>
                <TextField
                  value={message}
                  onChange={e => {
                    setMessage(e.target.value);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && !e.shiftKey && message) {
                      e.preventDefault();
                      await sendMessage();
                    }
                  }}
                  multiline
                  rows={2}
                  sx={{flex: 1, border: 0, "& fieldset": {border: 'none'},}}
                  InputProps={{sx: {fontSize: 12, border: 0, p: 1}}}
                  placeholder={"Write your message here..."}
                />
                <Box>
                  <IconButton onClick={sendMessage} color={"info"} disabled={loading}>
                    {loading ? <CircularProgress size={18}/> : <IconMessage size={18}/>}
                  </IconButton>
                </Box>
              </Stack>
            </> :
            <>
              <Box>
                <Button variant={"contained"} onClick={createNewConversation}>
                  Start New Conversation
                </Button>
              </Box>
            </>
          }
        </Stack>
      </Stack>
    </>
  )
})